import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Loader2, Calendar, Users, Home, Clock, CheckCircle, 
  AlertTriangle, MapPin, CreditCard, MessageSquare, X,
  Shield, LogIn, Phone, Mail, XCircle, Edit2, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const UnifiedGuestPortal = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState(null);
  
  // Login form
  const [loginForm, setLoginForm] = useState({
    email: '',
    booking_id: searchParams.get('booking') || ''
  });
  
  // Booking data
  const [booking, setBooking] = useState(null);
  const [property, setProperty] = useState(null);
  const [host, setHost] = useState(null);
  const [isPaid, setIsPaid] = useState(false);
  const [requiresPayment, setRequiresPayment] = useState(false);
  const [canCancel, setCanCancel] = useState(false);
  const [paymentToken, setPaymentToken] = useState(null);
  
  // Cancellation dialog
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    // Check for existing session
    const savedToken = sessionStorage.getItem('guest_portal_token');
    const savedBookingId = sessionStorage.getItem('guest_portal_booking_id');
    if (savedToken && savedBookingId) {
      setToken(savedToken);
      setIsLoggedIn(true);
      fetchBookingDetails(savedBookingId, savedToken);
    }
    
    // Pre-fill booking ID from URL
    if (searchParams.get('booking')) {
      setLoginForm(prev => ({ ...prev, booking_id: searchParams.get('booking') }));
    }
  }, [searchParams]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.booking_id) {
      setError('Please enter your email and booking ID');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API_URL}/api/guest-booking/portal/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: loginForm.email,
          booking_id: loginForm.booking_id
        })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Login failed');
      }
      
      const data = await res.json();
      setToken(data.token);
      setBooking(data.booking);
      setProperty(data.property);
      setIsLoggedIn(true);
      
      // Save session
      sessionStorage.setItem('guest_portal_token', data.token);
      sessionStorage.setItem('guest_portal_booking_id', data.booking.booking_id);
      
      // Fetch full details
      fetchBookingDetails(data.booking.booking_id, data.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookingDetails = async (bookingId, authToken) => {
    try {
      const res = await fetch(`${API_URL}/api/guest-booking/portal/booking/${bookingId}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (!res.ok) {
        throw new Error('Failed to load booking details');
      }
      
      const data = await res.json();
      setBooking(data.booking);
      setProperty(data.property);
      setHost(data.host);
      setIsPaid(data.is_paid);
      setRequiresPayment(data.requires_payment);
      setCanCancel(data.can_cancel);
      setPaymentToken(data.payment_token);
    } catch (err) {
      setError(err.message);
      handleLogout();
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('guest_portal_token');
    sessionStorage.removeItem('guest_portal_booking_id');
    setToken(null);
    setIsLoggedIn(false);
    setBooking(null);
    setProperty(null);
  };

  const handleCancelBooking = async () => {
    if (!cancelReason.trim()) {
      alert('Please provide a reason for cancellation');
      return;
    }
    
    setCancelling(true);
    try {
      const res = await fetch(`${API_URL}/api/guest-booking/booking/${booking.booking_id}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reason: cancelReason,
          cancelled_by: 'guest'
        })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Cancellation failed');
      }
      
      // Refresh booking details
      fetchBookingDetails(booking.booking_id, token);
      setShowCancelDialog(false);
      setCancelReason('');
    } catch (err) {
      alert(err.message);
    } finally {
      setCancelling(false);
    }
  };

  const goToPayment = () => {
    if (paymentToken) {
      window.location.href = `/guest/booking/${paymentToken}`;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      provisional: 'bg-amber-100 text-amber-700',
      waitlist: 'bg-purple-100 text-purple-700',
      confirmed: 'bg-emerald-100 text-emerald-700',
      paid: 'bg-emerald-100 text-emerald-700',
      'checked-in': 'bg-blue-100 text-blue-700',
      'checked-out': 'bg-gray-100 text-gray-600',
      cancelled: 'bg-red-100 text-red-600',
    };
    return styles[status] || 'bg-gray-100 text-gray-600';
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-pink-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Home className="w-8 h-8 text-pink-500" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800">Guest Portal</h1>
            <p className="text-slate-500 mt-2">Access your booking information</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <Label className="text-sm font-medium text-slate-700">Email Address</Label>
                <Input 
                  type="email"
                  value={loginForm.email}
                  onChange={e => setLoginForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="your@email.com"
                  className="mt-1"
                  data-testid="guest-portal-email"
                  required
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium text-slate-700">Booking ID</Label>
                <Input 
                  value={loginForm.booking_id}
                  onChange={e => setLoginForm(prev => ({ ...prev, booking_id: e.target.value }))}
                  placeholder="book_xxxxxx"
                  className="mt-1 font-mono"
                  data-testid="guest-portal-booking-id"
                  required
                />
                <p className="text-xs text-slate-400 mt-1">Found in your booking confirmation email</p>
              </div>
              
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {error}
                </div>
              )}
              
              <Button 
                type="submit" 
                disabled={loading}
                className="w-full bg-pink-500 hover:bg-pink-600 text-white py-5"
                data-testid="guest-portal-login-btn"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Accessing...</>
                ) : (
                  <><LogIn className="w-4 h-4 mr-2" /> Access My Booking</>
                )}
              </Button>
            </form>
            
            <div className="mt-6 pt-6 border-t border-slate-100">
              <div className="flex items-center gap-2 text-xs text-slate-400 justify-center">
                <Shield className="w-3.5 h-3.5" />
                Secure access - your data is protected
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (!booking) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-pink-500" />
      </div>
    );
  }

  // Main Portal View
  return (
    <div className="min-h-screen bg-slate-50" data-testid="unified-guest-portal">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-500 to-rose-500 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl font-bold">Guest Portal</h1>
              <p className="text-pink-100 text-sm">{booking.guest_email}</p>
            </div>
            <button 
              onClick={handleLogout}
              className="text-pink-200 hover:text-white text-sm"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Status Banner */}
        {booking.status === 'cancelled' && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
            <XCircle className="w-5 h-5 text-red-500" />
            <div>
              <p className="font-semibold text-red-800">Booking Cancelled</p>
              {booking.cancellation_reason && (
                <p className="text-red-600 text-sm">Reason: {booking.cancellation_reason}</p>
              )}
            </div>
          </div>
        )}
        
        {requiresPayment && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="font-semibold text-amber-800">Payment Required</p>
                  <p className="text-amber-600 text-sm">Complete payment to confirm your booking</p>
                </div>
              </div>
              <Button onClick={goToPayment} className="bg-amber-500 hover:bg-amber-600">
                <CreditCard className="w-4 h-4 mr-2" /> Pay Now
              </Button>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Booking Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              {property?.image_url && (
                <img 
                  src={property.image_url} 
                  alt={property?.name}
                  className="w-full h-48 object-cover"
                />
              )}
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">{property?.name}</h2>
                    {property && (
                      <p className="text-slate-500 flex items-center gap-1 mt-1">
                        <MapPin className="w-4 h-4" />
                        {property.city}, {property.country}
                      </p>
                    )}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(booking.status)}`}>
                    {booking.status}
                  </span>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4 mt-6">
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                    <Calendar className="w-5 h-5 text-pink-500" />
                    <div>
                      <p className="text-xs text-slate-500">Check-in</p>
                      <p className="font-semibold text-slate-800">{formatDate(booking.check_in)}</p>
                      <p className="text-xs text-slate-500">{property?.check_in_time || '15:00'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                    <Calendar className="w-5 h-5 text-blue-500" />
                    <div>
                      <p className="text-xs text-slate-500">Check-out</p>
                      <p className="font-semibold text-slate-800">{formatDate(booking.check_out)}</p>
                      <p className="text-xs text-slate-500">{property?.check_out_time || '11:00'}</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-6 mt-6 pt-6 border-t border-slate-100">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">{booking.num_guests} guest(s)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600 font-semibold">
                      {property?.currency || 'KES'} {booking.total_price?.toLocaleString()}
                    </span>
                    {isPaid && <CheckCircle className="w-4 h-4 text-emerald-500" />}
                  </div>
                </div>
              </div>
            </div>

            {/* Property Details */}
            {isPaid && property && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h3 className="font-bold text-slate-800 mb-4">Property Details</h3>
                
                {property.address && (
                  <div className="mb-4">
                    <p className="text-sm text-slate-500 mb-1">Address</p>
                    <p className="text-slate-800">{property.address}, {property.city}, {property.country}</p>
                  </div>
                )}
                
                {property.check_in_instructions && (
                  <div className="mb-4 p-4 bg-blue-50 rounded-xl">
                    <p className="text-sm font-semibold text-blue-800 mb-2">Check-in Instructions</p>
                    <p className="text-blue-700 text-sm whitespace-pre-wrap">{property.check_in_instructions}</p>
                  </div>
                )}
                
                {property.house_rules && (
                  <div className="p-4 bg-amber-50 rounded-xl">
                    <p className="text-sm font-semibold text-amber-800 mb-2">House Rules</p>
                    <p className="text-amber-700 text-sm whitespace-pre-wrap">{property.house_rules}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <h3 className="font-bold text-slate-800 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                {requiresPayment && (
                  <Button 
                    onClick={goToPayment} 
                    className="w-full bg-pink-500 hover:bg-pink-600"
                  >
                    <CreditCard className="w-4 h-4 mr-2" /> Complete Payment
                  </Button>
                )}
                
                {host?.email && (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => window.location.href = `mailto:${host.email}`}
                  >
                    <Mail className="w-4 h-4 mr-2" /> Contact Host
                  </Button>
                )}
                
                {canCancel && booking.status !== 'cancelled' && (
                  <Button 
                    variant="outline" 
                    className="w-full text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => setShowCancelDialog(true)}
                  >
                    <XCircle className="w-4 h-4 mr-2" /> Cancel Booking
                  </Button>
                )}
              </div>
            </div>

            {/* Host Info */}
            {host && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h3 className="font-bold text-slate-800 mb-4">Your Host</h3>
                <p className="font-semibold text-slate-700">{host.name}</p>
                {isPaid && host.email && (
                  <p className="text-slate-500 text-sm flex items-center gap-2 mt-2">
                    <Mail className="w-3.5 h-3.5" /> {host.email}
                  </p>
                )}
                {isPaid && host.phone && (
                  <p className="text-slate-500 text-sm flex items-center gap-2 mt-1">
                    <Phone className="w-3.5 h-3.5" /> {host.phone}
                  </p>
                )}
              </div>
            )}

            {/* Booking Reference */}
            <div className="bg-slate-100 rounded-2xl p-6">
              <p className="text-xs text-slate-500 mb-1">Booking Reference</p>
              <p className="font-mono text-slate-800 font-semibold">{booking.booking_id}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Dialog */}
      {showCancelDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800">Cancel Booking</h3>
              <button onClick={() => setShowCancelDialog(false)}>
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            
            <p className="text-slate-600 mb-4">
              Are you sure you want to cancel your booking at <strong>{property?.name}</strong>?
            </p>
            
            <div className="mb-4">
              <Label className="text-sm font-medium">Reason for cancellation *</Label>
              <textarea
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                className="w-full mt-1 p-3 border border-slate-200 rounded-lg text-sm resize-none"
                rows={3}
                placeholder="Please provide a reason..."
                data-testid="cancel-reason-input"
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowCancelDialog(false)}
                className="flex-1"
              >
                Keep Booking
              </Button>
              <Button
                onClick={handleCancelBooking}
                disabled={cancelling || !cancelReason.trim()}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                data-testid="confirm-cancel-btn"
              >
                {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cancel Booking'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedGuestPortal;
